import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, ReferenceDot,
} from "recharts";
import { Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "@/lib/i18n";
import { fmtCurrency } from "@/components/portfolio/_format";
import InfoTooltip from "@/components/portfolio/InfoTooltip";
import api from "@/lib/api";
import {
  computeYDomainWithHeadroom,
  resolveAxisTitles,
  useContainerIsMobile,
  formatIndexTick,
  formatCurrencyTick,
  performanceEmptyState,
} from "@/components/portfolio/PortfolioGrowthChartHelpers";

/**
 * Phase 8.E.3 — Premium Portfolio Performance chart.
 *
 * Three series (dual-axis):
 *   • Portfolio         — cyan, primary, LEFT axis (normalized index, base=100)
 *   • S&P 500 (^GSPC)   — indigo dashed, LEFT axis (normalized index, base=100)
 *   • Est. Fair Value   — neon green, RIGHT axis (currency; gaps preserved)
 *
 * KPIs above chart: Portfolio Return, S&P 500 Return, Relative Performance,
 * Method badge, Data Quality chip.
 *
 * Data source: GET /api/portfolio/performance?range=<selected>
 *   • Cash-flow-adjusted TWR (canonical) or Modified Dietz (fallback);
 *     never silently mixed — response `performance_method` is displayed.
 *   • Returns already come as PERCENTAGE values (12.34 = 12.34 %).
 *   • Chart NEVER recomputes TWR client-side.
 *
 * Backward-compat: still accepts `snapshots` + `summary` props from
 * Portfolio.js so an initial render before the /performance call resolves
 * (or a range with no data) never crashes.
 */

const RANGES = ["1M", "3M", "6M", "YTD", "1Y", "ALL"];

// Persist selected range across in-session refreshes (per user tab).
const RANGE_STORAGE_KEY = "stockaily_portfolio_perf_range";

// Chart palette — cyan portfolio / indigo benchmark / neon-green FV.
const COLOR_PORTFOLIO = "#3DEBFF";
const COLOR_BENCHMARK = "#7C6BFF";
const COLOR_FAIR_VALUE = "#2BFF8A";

function formatDate(isoDate) {
  if (!isoDate) return "";
  try { return new Date(isoDate).toLocaleDateString(); } catch { return isoDate; }
}

function formatPct(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${Number(v).toFixed(digits)}%`;
}

// Phase 8.E.4 visual-review v3 — the previous viewport `useIsMobile`
// hook has been replaced by `useContainerIsMobile` (imported from
// PortfolioGrowthChartHelpers). It observes the rendered chart card
// via ResizeObserver so the "mobile" layout kicks in when the
// container itself is narrow (e.g. inside the Owner Console preview
// panel), not only when the browser window is narrow.

function formatIndex(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toFixed(digits);
}

function loadStoredRange() {
  if (typeof window === "undefined") return "3M";
  try {
    const stored = localStorage.getItem(RANGE_STORAGE_KEY);
    if (stored && RANGES.includes(stored)) return stored;
  } catch { /* swallow */ }
  return "3M";
}

function saveStoredRange(range) {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(RANGE_STORAGE_KEY, range); } catch { /* swallow */ }
}

/**
 * Extended glass tooltip (spec 4b):
 * Date • Portfolio index/return-since-start/value •
 * S&P 500 index/return-since-start/price •
 * Relative performance • Fair Value (with partial indicator) •
 * Cash-flow event, if any.
 */
function GlassTooltip({
  active, payload, label, t, rangeLabel,
  portfolioReturnPct, benchmarkReturnPct, relativePctPoints,
  fvCoveragePct, cashFlowsByDate,
  // Phase 8.E.4 visual-review v3 — container-mobile-aware sizing.
  isMobile = false,
  // Phase 8.E.4 visual-review v4 — hard clamp so tooltip never
  // exceeds the actual card width. We subtract a safety margin for
  // the card padding + Recharts' offset.
  containerWidth = 0,
}) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  const cf = cashFlowsByDate?.[label];
  const partialFv = fvCoveragePct !== null && fvCoveragePct !== undefined && fvCoveragePct < 100;

  const rows = [];
  if (row.portfolio_index !== null && row.portfolio_index !== undefined) {
    rows.push({
      color: COLOR_PORTFOLIO, name: t("portfolio.chart.tt_portfolio_index"),
      value: formatIndex(row.portfolio_index),
      testId: "tt-portfolio-index",
    });
  }
  // Value-mode charts deliberately have no portfolio index. Their currency
  // value must still appear in the tooltip (including a zero-value close).
  if (row.portfolio_value !== null && row.portfolio_value !== undefined) {
    rows.push({
      color: COLOR_PORTFOLIO,
      name: t("portfolio.chart.tt_portfolio_value"),
      value: fmtCurrency(row.portfolio_value, { ifNull: "—" }),
      testId: "tt-portfolio-value",
      subtle: row.portfolio_index !== null && row.portfolio_index !== undefined,
    });
  }
  if (row.benchmark_index !== null && row.benchmark_index !== undefined) {
    rows.push({
      color: COLOR_BENCHMARK, name: t("portfolio.chart.tt_sp500_index"),
      value: formatIndex(row.benchmark_index),
      testId: "tt-benchmark-index",
    });
    if (row.benchmark_value !== null && row.benchmark_value !== undefined) {
      rows.push({
        color: COLOR_BENCHMARK,
        name: t("portfolio.chart.tt_sp500_price"),
        value: formatIndex(row.benchmark_value, 2),
        testId: "tt-benchmark-price",
        subtle: true,
      });
    }
  }
  if (row.fair_value !== null && row.fair_value !== undefined) {
    rows.push({
      color: COLOR_FAIR_VALUE,
      name: t("portfolio.chart.tt_fair_value"),
      value: fmtCurrency(row.fair_value, { ifNull: "—" }),
      testId: "tt-fair-value",
      badge: partialFv ? t("portfolio.chart.tt_fair_value_partial") : null,
    });
  }

  // Compute the safe max-width — clamped to the actual container so
  // the tooltip cannot escape the card even when the cursor is near
  // the right edge. Fallback to 240 px if container width isn't
  // available yet (initial render).
  const cardSafeMaxPx = containerWidth > 0
    ? Math.max(160, Math.round(containerWidth - 48))
    : (isMobile ? 220 : 300);
  const tooltipStyle = isMobile
    ? { maxWidth: `${Math.min(240, cardSafeMaxPx)}px`, minWidth: "148px" }
    : { maxWidth: `${Math.min(320, cardSafeMaxPx)}px`, minWidth: "220px" };

  return (
    <div
      data-testid="portfolio-perf-tooltip"
      className="rounded-xl border border-white/10 bg-black/70 backdrop-blur-xl px-3 py-2.5 shadow-2xl text-[10.5px] sm:text-xs"
      style={tooltipStyle}
    >
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
        {t("portfolio.chart.tt_date")}: {formatDate(label)}
      </div>
      {rows.map((r) => (
        <div key={r.testId} data-testid={r.testId} className="flex items-center gap-2 text-xs tabular py-0.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: r.color }} />
          <span className={r.subtle ? "text-muted-foreground/70" : "text-muted-foreground"}>{r.name}</span>
          <span className="ml-auto text-foreground font-medium">{r.value}</span>
          {r.badge ? (
            <span className="ml-1 rounded-full border border-white/15 px-1.5 py-[1px] text-[9px] uppercase text-muted-foreground/80">
              {r.badge}
            </span>
          ) : null}
        </div>
      ))}
      {row.fair_value !== null && row.fair_value !== undefined ? (
        <div data-testid="tt-fv-analysis-date" className="mt-1 text-[10px] text-muted-foreground">
          {row.fv_analysis_as_of
            ? t("portfolio.chart.fv_analysis_as_of").replace("{date}", formatDate(row.fv_analysis_as_of))
            : t("portfolio.chart.fv_analysis_date_unknown")}
          {row.fv_carried_forward ? ` · ${t("portfolio.chart.fv_carried_forward")}` : ""}
        </div>
      ) : null}
      {/* Since-start returns + relative performance (period-level, not per-day). */}
      {(portfolioReturnPct !== null || benchmarkReturnPct !== null || relativePctPoints !== null) ? (
        <div className="mt-2 pt-2 border-t border-white/10 space-y-0.5">
          {portfolioReturnPct !== null && portfolioReturnPct !== undefined ? (
            <div className="flex items-center gap-2 text-[11px] tabular">
              <span className="text-muted-foreground">
                {t("portfolio.chart.tt_portfolio_return_since_start").replace("{range}", rangeLabel)}
              </span>
              <span className="ml-auto text-foreground" data-testid="tt-portfolio-return">
                {formatPct(portfolioReturnPct)}
              </span>
            </div>
          ) : null}
          {benchmarkReturnPct !== null && benchmarkReturnPct !== undefined ? (
            <div className="flex items-center gap-2 text-[11px] tabular">
              <span className="text-muted-foreground">
                {t("portfolio.chart.tt_sp500_return_since_start").replace("{range}", rangeLabel)}
              </span>
              <span className="ml-auto text-foreground" data-testid="tt-benchmark-return">
                {formatPct(benchmarkReturnPct)}
              </span>
            </div>
          ) : null}
          {relativePctPoints !== null && relativePctPoints !== undefined ? (
            <div className="flex items-center gap-2 text-[11px] tabular">
              <span className="text-muted-foreground">{t("portfolio.chart.tt_relative_performance")}</span>
              <span className="ml-auto text-foreground font-medium" data-testid="tt-relative">
                {formatPct(relativePctPoints)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
      {cf ? (
        <div className="mt-2 pt-2 border-t border-white/10 text-[11px] tabular flex items-center gap-2">
          <span className={cf.kind === "BUY" ? "text-[#3DEBFF]" : "text-[#FF3DA6]"}>
            {cf.kind === "BUY"
              ? t("portfolio.chart.tt_cash_flow_buy")
              : t("portfolio.chart.tt_cash_flow_sell")}
          </span>
          <span className="text-muted-foreground">{cf.tickers.join(", ")}</span>
          <span className="ml-auto text-foreground">{fmtCurrency(Math.abs(cf.net), { ifNull: "—" })}</span>
        </div>
      ) : null}
    </div>
  );
}

/** KPI card. */
function KPICard({ label, value, tone = "neutral", hint, testId, unavailable = false, fullWidth = false }) {
  const toneClass =
    tone === "positive" ? "text-[#2BFF8A]" :
    tone === "negative" ? "text-[#FF3DA6]" :
    tone === "portfolio" ? "text-[#3DEBFF]" :
    tone === "benchmark" ? "text-[#7C6BFF]" :
    "text-foreground";
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border border-white/10 bg-black/25 backdrop-blur px-3 py-2.5 min-w-0 ${fullWidth ? "col-span-2" : ""}`}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground truncate">
        {label}
        {hint ? <InfoTooltip text={hint} testId={`${testId}-tip`} label={label} /> : null}
      </div>
      <div className={`mt-1 text-lg font-medium tabular truncate ${unavailable ? "text-muted-foreground/60 text-sm" : toneClass}`}>
        {value}
      </div>
    </div>
  );
}

/** Method-badge with rich tooltip. */
function MethodBadge({ method, fallbackUsed, dataQuality, t, isValueMode = false }) {
  const label =
    method === "TWR_DAILY" ? t("portfolio.chart.method_twr_daily") :
    method === "MODIFIED_DIETZ_FALLBACK" ? t("portfolio.chart.method_modified_dietz") :
    t("portfolio.chart.method_none");
  const explanation =
    method === "MODIFIED_DIETZ_FALLBACK"
      ? (isValueMode
          ? t("portfolio.chart.method_tooltip_dietz_value_chart")
          : t("portfolio.chart.method_tooltip_dietz"))
      : t("portfolio.chart.method_tooltip_twr");
  // Rev-2: for the fallback path, ALSO surface the baseline-scope note.
  const baselineScope = method === "MODIFIED_DIETZ_FALLBACK"
    ? t("portfolio.chart.method_tooltip_baseline_scope") : null;
  const scope = t("portfolio.chart.method_tooltip_scope");
  const color =
    method === "TWR_DAILY" ? "border-[#3DEBFF]/40 text-[#3DEBFF] bg-[rgba(61,235,255,0.08)]" :
    method === "MODIFIED_DIETZ_FALLBACK" ? "border-[#FFBE3D]/40 text-[#FFBE3D] bg-[rgba(255,190,61,0.08)]" :
    "border-white/15 text-muted-foreground";
  return (
    <TooltipProvider delayDuration={100}>
      <UITooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="portfolio-perf-method-badge"
            data-method={method}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium cursor-help ${color}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="max-w-xs text-xs leading-relaxed"
          data-testid="portfolio-perf-method-tooltip-content"
        >
          <div className="font-medium mb-1">{label}</div>
          <div className="text-muted-foreground mb-1.5">{explanation}</div>
          {baselineScope ? (
            <div className="text-[11px] text-muted-foreground/90 border-t border-white/10 pt-1.5 mb-1.5"
                 data-testid="portfolio-perf-method-tooltip-baseline-scope">
              {baselineScope}
            </div>
          ) : null}
          <div className="text-[11px] text-muted-foreground/80 border-t border-white/10 pt-1.5">{scope}</div>
        </TooltipContent>
      </UITooltip>
    </TooltipProvider>
  );
}

/** Data-quality chip. */
function QualityChip({ dataQuality, t, historyBuilding = false }) {
  const label =
    historyBuilding ? t("portfolio.chart.history_building_label") :
    dataQuality === "good" ? t("portfolio.chart.data_quality_good") :
    dataQuality === "partial" ? t("portfolio.chart.data_quality_partial") :
    t("portfolio.chart.data_quality_insufficient");
  const cls =
    historyBuilding ? "border-white/15 text-muted-foreground bg-white/[0.03]" :
    dataQuality === "good" ? "border-[#2BFF8A]/40 text-[#2BFF8A] bg-[rgba(43,255,138,0.08)]" :
    dataQuality === "partial" ? "border-[#FFBE3D]/40 text-[#FFBE3D] bg-[rgba(255,190,61,0.08)]" :
    "border-white/15 text-muted-foreground";
  return (
    <span
      data-testid="portfolio-perf-quality-chip"
      data-quality={dataQuality}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${cls}`}
    >
      {label}
    </span>
  );
}

/** Remember a notice choice for this account and browser-tab session only. */
function useNoticeDismissal(ownerId, kind, signature, previewMode) {
  const key = `stockaily:portfolio:notice:v1:${ownerId || "memory"}:${kind}`;
  const [choice, setChoice] = useState(null);
  let saved = null;
  if (ownerId) {
    try { saved = window.sessionStorage.getItem(key); } catch { /* storage unavailable */ }
  }
  const selected = choice?.key === key ? choice.signature : saved;
  const hidden = !previewMode && selected === signature;
  const choose = (hide) => {
    const value = hide ? signature : null;
    setChoice({ key, signature: value });
    if (ownerId && !previewMode) {
      try {
        if (hide) window.sessionStorage.setItem(key, value);
        else window.sessionStorage.removeItem(key);
      } catch { /* in-memory choice still works */ }
    }
  };
  return [hidden, () => choose(true), () => choose(false)];
}

export default function PortfolioGrowthChart({
  snapshots = [],
  summary = null,
  refreshKey = "",
  refreshPending = false,
  // Phase 8.E.4 — Safe Owner-only preview injection hook.
  //
  // When ``fetchOverride`` is provided (Owner Console preview only),
  // the chart uses it instead of calling /api/portfolio/performance
  // directly. This lets the Owner Console feed the seeded demo
  // user's payload (or an in-memory transformed variant for
  // degraded-state simulations) WITHOUT any visual change to the
  // chart itself and WITHOUT any impersonation.
  //
  // Signature: async (rangeKey) => performancePayload | null
  fetchOverride = null,
  // Phase 8.WLNAV.v1.6 — Locked Portfolio Preview mode.
  //
  // When previewMode=true (LockedPortfolioPreview only):
  //   • Range is locked to "1Y" (setRange is never invoked)
  //   • Range tabs 1M/3M/6M/YTD/ALL and Hide Fair Value render as
  //     visibly locked (opacity + Lock icon) and are non-interactive
  //   • Recharts <Tooltip> is not mounted — no crosshair, no hover
  //     tooltip, no interactive dots on hover
  //   • A static tooltip overlay is rendered near the right edge of
  //     the plot, matching the real GlassTooltip styling, pinned to
  //     the last data point (curated demo behavior)
  previewMode = false,
}) {
  const { t } = useTranslation();
  // Preview mode forces "1Y" and never touches the stored preference.
  const [range, setRange] = useState(previewMode ? "1Y" : loadStoredRange);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFairValue, setShowFairValue] = useState(true);
  const inflightRef = useRef(0);

  // Persist range selection across renders / refreshes.
  // Skipped in preview mode so previewing never overwrites the
  // Premium user's stored preference.
  useEffect(() => {
    if (previewMode) return;
    saveStoredRange(range);
  }, [range, previewMode]);

  // Fetch performance data from the backend whenever `range` changes.
  // The Owner Console preview may inject an override that returns a
  // payload from a different (seed-tag-gated) endpoint or from an
  // in-memory transformation.
  const fetchPerformance = useCallback(async (rangeKey) => {
    const myId = ++inflightRef.current;
    setLoading(true);
    setError(null);
    try {
      let payload = null;
      if (typeof fetchOverride === "function") {
        payload = await fetchOverride(rangeKey);
      } else {
        const res = await api.get("/portfolio/performance", { params: { range: rangeKey } });
        payload = res?.data?.performance || null;
      }
      if (myId !== inflightRef.current) return;   // stale
      setPerf(payload || null);
    } catch (err) {
      if (myId !== inflightRef.current) return;
      setPerf(null);
      // Silent for 401/402 — the Portfolio page already enforces gating.
      const status = err?.response?.status;
      if (status && status !== 401 && status !== 402) {
        setError("network");
      }
    } finally {
      if (myId === inflightRef.current) setLoading(false);
    }
  }, [fetchOverride]);

  useEffect(() => {
    setPerf(null); // do not retain the previous composition's graph while refetching
    fetchPerformance(range);
    return () => { inflightRef.current += 1; };
  }, [range, refreshKey, fetchPerformance]);

  // ── Build cash-flow lookup for the tooltip (BUY/SELL badges). ──
  // The endpoint's `cash_flow_summary` is aggregate-only; when we later
  // extend the API to emit per-day flows we'll wire them here. For now
  // the tooltip supports the {date, kind, tickers, net} shape and we
  // simply don't populate it (no fabricated data).
  const cashFlowsByDate = useMemo(() => ({}), []);

  // ── Merged chart data. ────────────────────────────────────────
  const chartData = useMemo(() => {
    if (!perf) return [];
    const pSeries = perf.portfolio_series || [];
    const bSeries = perf.benchmark_series || [];
    const fvSeries = perf.fair_value_series || [];
    const byDate = new Map();
    for (const row of pSeries) {
      byDate.set(row.date, {
        date: row.date,
        portfolio_index: row.index,
        portfolio_value: row.value,
        benchmark_index: null,
        benchmark_value: null,
        fair_value: null,
      });
    }
    for (const row of bSeries) {
      const rec = byDate.get(row.date) || { date: row.date };
      rec.benchmark_index = row.index;
      rec.benchmark_value = row.value;
      byDate.set(row.date, rec);
    }
    for (const row of fvSeries) {
      const rec = byDate.get(row.date) || { date: row.date };
      rec.fair_value = row.value;
      rec.fv_analysis_as_of = row.analysis_as_of;
      rec.fv_carried_forward = row.analysis_is_carried_forward;
      byDate.set(row.date, rec);
    }
    return Array.from(byDate.values()).sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [perf]);

  // ── Derived flags for degraded states. ────────────────────────
  const dataQuality = refreshPending ? "insufficient" : (perf?.data_quality || "insufficient");
  // Phase 8.E.4 visual-review — the chart's semantic mode is dictated
  // by the backend:
  //   "index"       → linked TWR index, dual-axis (index left / FV right)
  //   "value"       → currency portfolio-value chart, single currency
  //                    axis; benchmark line hidden to avoid mixing units
  //   "unavailable" → no line rendered; degraded-state card shown
  const chartDisplayMode = perf?.chart_display_mode
    || (perf ? (perf.performance_method === "MODIFIED_DIETZ_FALLBACK" ? "value" : "index") : "unavailable");
  const isValueMode = chartDisplayMode === "value";
  const isReconstructed = perf?.history_source === "transactions_and_historical_prices";
  const missingReconstructionDates = perf?.history_reconstruction?.reason === "missing_purchase_dates";
  const hasHistoryNotice = isValueMode || isReconstructed;
  const isIndexMode = chartDisplayMode === "index";
  const hasPortfolioLine = isValueMode
    ? chartData.some(r => r.portfolio_value !== null && r.portfolio_value !== undefined)
    : chartData.some(r => r.portfolio_index !== null && r.portfolio_index !== undefined);
  const benchmarkStatus = perf?.benchmark?.status || null;
  // In value mode the normalized benchmark line is deliberately hidden
  // (mixing currency and index units on one axis would mislead).
  const hasBenchmarkLine = isIndexMode
    && benchmarkStatus === "present"
    && chartData.some(r => r.benchmark_index !== null && r.benchmark_index !== undefined);
  const fvCoveragePct = perf?.fair_value_coverage_pct ?? null;
  const hasFairValue = (fvCoveragePct || 0) > 0 && chartData.some(r => r.fair_value !== null && r.fair_value !== undefined);
  const fvCoverageChangeCount = perf?.fair_value_coverage_change_count ?? 0;
  const hasFvCoverageGaps = fvCoverageChangeCount > 0;
  const hasCarriedValuations = (perf?.fair_value_series || []).some(row => row.value != null && row.analysis_is_carried_forward);
  // A missing benchmark or a sparse chart must not hide a real portfolio return.
  const hasPortfolioReturn = perf?.portfolio_return_pct !== null && perf?.portfolio_return_pct !== undefined;
  const benchmarkUnavailable = perf?.benchmark_return_pct === null || perf?.benchmark_return_pct === undefined;
  const isInsufficient = dataQuality === "insufficient" || (!hasPortfolioLine && !hasPortfolioReturn);
  const isPartial = dataQuality === "partial";

  // Phase 8.E.4 visual-review v4 — callback-ref pattern for
  // synchronous container-width measurement (no first-paint race).
  // Also returns the actual container width so the tooltip can clamp
  // its max-width to the card, not the viewport.
  const [chartContainerRef, isMobile, containerWidth] = useContainerIsMobile(640);
  const {
    indexAxisTitle, valueAxisTitle, fvAxisTitle,
    yAxisWidthIndex, yAxisWidthCurrency,
  } = resolveAxisTitles({ isMobile, t });
  const chartRightMargin = (isValueMode || !(showFairValue && hasFairValue))
    ? (isMobile ? 4 : 8)
    : (isMobile ? 6 : 16);
  const chartLeftMargin  = isMobile ? 0 : 8;
  // Locale for the currency-tick formatter (EN/DE only for now).
  const currencyLocale = ((typeof document !== "undefined" && document.documentElement && document.documentElement.lang) || "en")
    .toLowerCase().startsWith("de") ? "de" : "en";
  // Phase 8.E.4 visual-review v7 — S&P 500 dash pattern is tighter
  // on mobile to prevent the "stepped" look that shows up when the
  // chart width is small (fewer pixels per dash rhythm). Desktop
  // keeps the polished 6/4 pattern that was already approved.
  const benchmarkDash = isMobile ? "3 3" : "6 4";

  // ── Y-axis domains — STRICT per-unit isolation ──────────────────
  // Phase 8.E.4 visual-review v3 (issue #3): each axis receives ONLY
  // values in its own unit system.
  //
  //   TWR / index mode:
  //     left  axis (index)    ← portfolio_index, benchmark_index only
  //     right axis (currency) ← fair_value only
  //   Dietz / value mode:
  //     single axis (currency) ← portfolio_value, fair_value only
  //
  // Mixing currency values (10 000 €) into an index domain (80–160)
  // produces the "4000001" / "1938564" corruption seen previously.
  const indexAxisValues    = isValueMode ? [] : chartData.flatMap((r) => [r.portfolio_index, r.benchmark_index]);
  const currencyAxisValues = isValueMode
    ? chartData.flatMap((r) => [r.portfolio_value, r.fair_value])
    : chartData.map((r) => r.fair_value);
  const indexDomain    = computeYDomainWithHeadroom(indexAxisValues);
  const currencyDomain = computeYDomainWithHeadroom(currencyAxisValues);

  const portfolioReturnPct = perf?.portfolio_return_pct ?? null;
  const benchmarkReturnPct = perf?.benchmark_return_pct ?? null;
  const relativePctPoints  = perf?.relative_performance_pct_points ?? null;

  // Phase 8.E.3 rev-2 — baseline surface (never exposes internal reason codes).
  const baselineDate = perf?.performance_history_start_date ?? null;
  const preBaselineExcludedCount = Array.isArray(perf?.excluded_pre_baseline_snapshot_dates)
    ? perf.excluded_pre_baseline_snapshot_dates.length : 0;
  const hasStableBaseline = Boolean(baselineDate);
  const snapshotDays = perf?.valid_snapshot_days ?? 0;
  const excludedPriceDays = perf?.diagnostics?.excluded_price_snapshots || [];
  const hasMissingPriceDays = excludedPriceDays.some((row) => !baselineDate || !row.date_key || row.date_key >= baselineDate)
    || (!excludedPriceDays.length && (perf?.diagnostics?.excluded_price_snapshot_count || 0) > 0);
  const historyBuilding = isValueMode && !hasMissingPriceDays
    && (perf?.daily_coverage_ratio ?? 1) < 0.7;
  const hasDataNotices = Boolean(hasFvCoverageGaps || hasCarriedValuations
    || (benchmarkUnavailable && perf) || (isPartial && !historyBuilding));
  // Counts/dates/types identify a materially changed issue. Daily prices,
  // analysis timestamps and the growing history count do not reopen notices.
  const dataNoticeSignature = JSON.stringify({
    fvGaps: hasFvCoverageGaps ? fvCoverageChangeCount : 0,
    fvGapDates: hasFvCoverageGaps ? (perf?.fair_value_series || [])
      .filter((row) => row.coverage_changed).map((row) => row.date) : [],
    carried: hasCarriedValuations,
    benchmark: benchmarkUnavailable ? (perf?.benchmark?.status || "unavailable") : null,
    partial: isPartial && !historyBuilding,
    priceGaps: excludedPriceDays.filter((row) => !baselineDate || !row.date_key || row.date_key >= baselineDate)
      .map((row) => [row.date_key, row.reason]),
  });
  const [historyNoticeHidden, hideHistoryNotice, showHistoryNotice] = useNoticeDismissal(
    perf?.user_id, "history", isReconstructed ? "reconstructed-history-v1" : "history-information-v2", previewMode,
  );
  const [dataNoticesHidden, hideDataNotices, showDataNotices] = useNoticeDismissal(
    perf?.user_id, "data", dataNoticeSignature, previewMode,
  );
  const hiddenNoticeCount = Number(hasHistoryNotice && historyNoticeHidden)
    + Number(hasDataNotices && dataNoticesHidden);
  const emptyState = performanceEmptyState(perf, refreshPending);
  const emptyCopy = {
    updating: ["updating_title", "updating_body"],
    missing_dates: ["missing_dates_title", "missing_dates_body"],
    transactions: ["transactions_title", "transactions_body"],
    history: ["insufficient_history_title", "insufficient_history_body"],
    baseline: ["unstable_baseline_title", "unstable_baseline_body"],
  }[emptyState];

  // Legend labels resolved for the current locale (used by tooltip too).
  const rangeLabel = range;

  const onRangeClick = (r) => {
    if (previewMode) return;               // preview: range is locked to 1Y
    if (r !== range) setRange(r);
  };

  // ── Insufficient card (no chart) ──────────────────────────────
  if (isInsufficient && !loading) {
    // Distinguish "no history at all" from "history exists but baseline
    // isn't stable yet" — different friendly copy, no internal codes.
    const showUnstableBaseline = emptyState === "baseline";
    return (
      <div
        data-testid="portfolio-growth-chart-card"
        className="relative overflow-hidden rounded-2xl bg-card/60 backdrop-blur-xl border border-white/10 shadow-[var(--app-shadow-elev-1)] p-4 sm:p-6"
      >
        <ChartHeader
          t={t} range={range} onRangeClick={onRangeClick} loading={loading}
          method={perf?.performance_method} fallbackUsed={perf?.fallback_used}
          dataQuality={dataQuality}
          portfolioReturnPct={null} benchmarkReturnPct={null} relativePctPoints={null}
          hasBenchmarkLine={false}
          showFairValueToggle={false}
          showFairValue={showFairValue} setShowFairValue={setShowFairValue}
          baselineDate={null} preBaselineExcludedCount={0}
        />
        <div className="h-64 sm:h-80 flex flex-col items-center justify-center text-center gap-2"
             data-testid={showUnstableBaseline ? "portfolio-perf-unstable-baseline-state" : "portfolio-perf-insufficient-state"}>
          <div className="text-sm text-foreground/80">
            {t(`portfolio.chart.${emptyCopy[0]}`)}
          </div>
          <div className="text-xs max-w-md text-muted-foreground">
            {t(`portfolio.chart.${emptyCopy[1]}`)}
          </div>
          {emptyState === "missing_dates" ? (
            <div className="text-xs text-muted-foreground" data-testid="portfolio-perf-missing-date-tickers">
              {Array.from(new Set((perf?.diagnostics?.transaction_classifications || [])
                .filter((row) => row.classification === "ambiguous_excluded")
                .map((row) => row.ticker).filter(Boolean))).join(", ")}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────
  // Phase 8.E.4 visual-review v4 — callback-ref goes on the outer
  // card so measurements start with the actual rendered width, not
  // a delayed post-effect measurement.
  return (
    <div
      ref={chartContainerRef}
      data-testid="portfolio-growth-chart-card"
      className={`relative overflow-hidden rounded-2xl bg-card/60 backdrop-blur-xl border border-white/10 shadow-[var(--app-shadow-elev-1)] ${isMobile ? "p-2.5" : "p-4 sm:p-6"}`}
    >
      <ChartHeader
        t={t} range={range} onRangeClick={onRangeClick} loading={loading}
        method={perf?.performance_method} fallbackUsed={perf?.fallback_used}
        dataQuality={dataQuality}
        portfolioReturnPct={portfolioReturnPct}
        benchmarkReturnPct={benchmarkReturnPct}
        relativePctPoints={relativePctPoints}
        hasBenchmarkLine={hasBenchmarkLine}
        showFairValueToggle={hasFairValue}
        showFairValue={showFairValue} setShowFairValue={setShowFairValue}
        baselineDate={baselineDate}
        preBaselineExcludedCount={preBaselineExcludedCount}
        isValueMode={isValueMode}
        historyBuilding={historyBuilding}
        isMobile={isMobile}
        previewMode={previewMode}
      />

      {/* Phase 8.E.4 visual-review — value-mode subtitle so the operator
          understands the chart is now a portfolio-value line, not a
          performance index. */}
      {hiddenNoticeCount > 0 ? (
        <div className="mb-2 flex justify-end">
          <button type="button" data-testid="portfolio-notices-show"
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={() => { showHistoryNotice(); showDataNotices(); }}>
            {t("portfolio.chart.show_notices").replace("{n}", String(hiddenNoticeCount))}
          </button>
        </div>
      ) : null}
      {hasHistoryNotice && !historyNoticeHidden ? (
        <div
          data-testid="portfolio-perf-value-mode-notice"
          className="mb-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-muted-foreground space-y-1"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="font-medium text-foreground/90">{t(isReconstructed ? "portfolio.chart.reconstructed_title" : "portfolio.chart.history_building_title")}</div>
            {!previewMode ? (
              <button type="button" onClick={hideHistoryNotice} data-testid="portfolio-history-notice-hide"
                aria-label={t("portfolio.chart.hide_history_notice")}
                className="shrink-0 text-xs text-muted-foreground hover:text-foreground rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
                {t("portfolio.chart.hide_notice")}
              </button>
            ) : null}
          </div>
          <div>{t(baselineDate ? "portfolio.chart.history_progress" : "portfolio.chart.history_progress_no_date")
            .replace("{date}", baselineDate ? formatDate(baselineDate) : "")
            .replace("{n}", String(snapshotDays))}</div>
          <div>{t(isReconstructed ? "portfolio.chart.reconstructed_body" : "portfolio.chart.history_building_body")}</div>
          {missingReconstructionDates ? <div>{t("portfolio.chart.reconstruction_dates_hint")}</div> : null}
          {!isReconstructed ? <div>{t("portfolio.chart.history_benchmark_body")}</div> : null}
        </div>
      ) : null}

      {/* Phase 8.LP.LR follow-up (2026-02 Staging regression) — degraded-
          state notices consolidated into ONE amber block so three
          overlapping warnings (coverage-gap + benchmark-unavailable +
          partial-data) no longer stack into three separate rows. All
          per-notice test IDs are preserved so e2e regression continues
          to assert each distinct message independently. */}
      {hasDataNotices && !dataNoticesHidden ? (
        <div
          data-testid="portfolio-perf-degraded-notices"
          className={`mb-2 relative rounded-md border border-amber-300/40 bg-amber-100/10 px-3 py-2 text-[11px] text-amber-200/90 space-y-1 ${previewMode ? "" : "pr-20"}`}
        >
          {!previewMode ? (
            <button type="button" onClick={hideDataNotices} data-testid="portfolio-data-notices-hide"
              aria-label={t("portfolio.chart.hide_data_notices")}
              className="absolute top-2 right-3 text-xs text-amber-200/80 hover:text-amber-100 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
              {t("portfolio.chart.hide_notice")}
            </button>
          ) : null}
          {hasCarriedValuations ? (
            <div data-testid="portfolio-perf-carried-valuation-notice">
              {t("portfolio.chart.fv_carried_forward_notice")}
            </div>
          ) : null}
          {hasFvCoverageGaps ? (
            <div
              data-testid="portfolio-perf-fv-coverage-gap-notice"
              data-coverage-change-count={fvCoverageChangeCount}
            >
              <span className="font-medium">{t("portfolio.chart.fv_coverage_gap_title") || "Fair-Value line has coverage gaps"}:</span>{" "}
              {(t("portfolio.chart.fv_coverage_gap_body")
                || "The Fair-Value line is drawn only across days whose covered holdings match. {n} coverage change(s) were detected and are rendered as gaps so sums over different holding sets are not compared.")
                .replace("{n}", String(fvCoverageChangeCount))}
            </div>
          ) : null}
          {benchmarkUnavailable && perf ? (
            <div data-testid="portfolio-perf-benchmark-unavailable-notice">
              {t("portfolio.chart.benchmark_unavailable_notice")}
            </div>
          ) : null}
          {isPartial && !historyBuilding ? (
            <div data-testid="portfolio-perf-partial-notice">
              {t("portfolio.chart.partial_data_notice")}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Phase 8.E.3 rev-2 — restrained baseline disclosure (informational,
          kept separate from the amber degraded-state block above). */}
      {hasStableBaseline && (!isValueMode || preBaselineExcludedCount > 0) ? (
        <div className="mb-2 text-[11px] text-muted-foreground leading-relaxed"
             data-testid="portfolio-perf-baseline-note">
          {!isValueMode ? t("portfolio.chart.baseline_note").replace("{date}", formatDate(baselineDate)) : null}
          {preBaselineExcludedCount > 0 ? (
            <>
              {" "}
              <span data-testid="portfolio-perf-baseline-excluded-note">
                {t("portfolio.chart.baseline_setup_excluded_note")}
              </span>
            </>
          ) : null}
        </div>
      ) : null}

      <div className={`${isMobile ? "h-80" : "h-72 sm:h-80"} ${previewMode ? "relative" : ""}`}>
        {chartData.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: chartRightMargin, left: chartLeftMargin, bottom: 0 }}>
              <CartesianGrid stroke="#2A2B33" strokeOpacity={0.55} vertical={false} />
              <XAxis
                dataKey="date"
                axisLine={false} tickLine={false}
                tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isMobile ? 10 : 11 }}
                minTickGap={isMobile ? 64 : 48}
              />
              {isValueMode ? (
                // Value mode — single currency axis on the left. Both
                // Portfolio Value AND Fair Value share this axis so
                // its title reads "Portfolio & Fair Value ($)". No
                // color scheme is applied (neutral currency axis).
                <YAxis
                  yAxisId="currency"
                  axisLine={false} tickLine={false}
                  tick={{ fill: "rgba(255,255,255,0.65)", fontSize: isMobile ? 10 : 11 }}
                  width={yAxisWidthCurrency}
                  domain={currencyDomain}
                  tickCount={isMobile ? 4 : 5}
                  tickFormatter={(v) => formatCurrencyTick(v, currencyLocale)}
                  label={valueAxisTitle ? {
                    value: valueAxisTitle,
                    angle: -90, position: "insideLeft",
                    style: { fill: "rgba(255,255,255,0.55)", fontSize: 10 },
                  } : undefined}
                />
              ) : (
                <>
                  <YAxis
                    yAxisId="index"
                    axisLine={false} tickLine={false}
                    // Phase 8.E.4 visual-review v6 — left index axis is
                    // now cyan (matches the Portfolio line). S&P 500
                    // shares this axis but stays visually secondary via
                    // its dashed purple line style.
                    tick={{ fill: COLOR_PORTFOLIO, fontSize: isMobile ? 10 : 11, fontWeight: 500 }}
                    width={yAxisWidthIndex}
                    domain={indexDomain}
                    tickCount={isMobile ? 4 : 5}
                    tickFormatter={formatIndexTick}
                    label={indexAxisTitle ? {
                      value: indexAxisTitle,
                      angle: -90, position: "insideLeft",
                      style: { fill: COLOR_PORTFOLIO, fontSize: 10, fontWeight: 500 },
                    } : undefined}
                  />
                  {showFairValue && hasFairValue ? (
                    <YAxis
                      yAxisId="currency"
                      orientation="right"
                      axisLine={false} tickLine={false}
                      // Right axis stays green — matches the Fair Value
                      // line for immediate visual pairing.
                      tick={{ fill: COLOR_FAIR_VALUE, fontSize: isMobile ? 10 : 11, fontWeight: 500 }}
                      width={yAxisWidthCurrency}
                      domain={currencyDomain}
                      tickCount={isMobile ? 4 : 5}
                      tickFormatter={(v) => formatCurrencyTick(v, currencyLocale)}
                      label={fvAxisTitle ? {
                        value: fvAxisTitle,
                        angle: 90, position: "insideRight",
                        style: { fill: COLOR_FAIR_VALUE, fontSize: 10, fontWeight: 500 },
                      } : undefined}
                    />
                  ) : null}
                </>
              )}
              {previewMode ? null : (
                <Tooltip
                  content={
                    <GlassTooltip
                      t={t}
                      rangeLabel={rangeLabel}
                      portfolioReturnPct={portfolioReturnPct}
                      benchmarkReturnPct={benchmarkReturnPct}
                      relativePctPoints={relativePctPoints}
                      fvCoveragePct={fvCoveragePct}
                      cashFlowsByDate={cashFlowsByDate}
                      isMobile={isMobile}
                      containerWidth={containerWidth}
                    />
                  }
                  cursor={{ stroke: "rgba(255,255,255,0.12)" }}
                  wrapperStyle={{ zIndex: 30, outline: "none", pointerEvents: "none" }}
                  allowEscapeViewBox={{ x: false, y: false }}
                  offset={isMobile ? 8 : 20}
                />
              )}
              <Legend
                iconType="plainline"
                wrapperStyle={{ color: "rgba(255,255,255,0.72)", fontSize: 11, paddingTop: 4 }}
              />
              {isValueMode ? (
                <Line
                  yAxisId="currency"
                  type="monotone"
                  name={t("portfolio.chart.tt_portfolio_value")}
                  dataKey="portfolio_value"
                  stroke={COLOR_PORTFOLIO}
                  strokeWidth={2.25}
                  dot={false}
                  connectNulls={false}
                  activeDot={{ r: 4, stroke: "#0A0A0C", strokeWidth: 2, fill: COLOR_PORTFOLIO }}
                  style={{ filter: "drop-shadow(0 0 10px rgba(61,235,255,0.35)) drop-shadow(0 0 22px rgba(61,235,255,0.18))" }}
                />
              ) : (
                <Line
                  yAxisId="index"
                  type="monotone"
                  name={t("portfolio.chart.portfolio_value")}
                  dataKey="portfolio_index"
                  stroke={COLOR_PORTFOLIO}
                  strokeWidth={2.25}
                  dot={false}
                  connectNulls={false}
                  activeDot={{ r: 4, stroke: "#0A0A0C", strokeWidth: 2, fill: COLOR_PORTFOLIO }}
                  style={{ filter: "drop-shadow(0 0 10px rgba(61,235,255,0.35)) drop-shadow(0 0 22px rgba(61,235,255,0.18))" }}
                />
              )}
              {hasBenchmarkLine ? (
                <Line
                  yAxisId="index"
                  type="monotone"
                  name={t("portfolio.chart.sp500_series")}
                  dataKey="benchmark_index"
                  stroke={COLOR_BENCHMARK}
                  strokeWidth={1.75}
                  strokeOpacity={0.9}
                  // Phase 8.E.4 visual-review v7 — desktop keeps the
                  // polished "6 4" rhythm; mobile switches to a
                  // tighter "3 3" pattern so the smaller pixel budget
                  // produces a visibly cleaner line instead of the
                  // "stepped" look reported at 390 px. butt caps keep
                  // dash edges exactly square either way.
                  strokeDasharray={benchmarkDash}
                  strokeLinecap="butt"
                  dot={false}
                  connectNulls={false}
                  activeDot={{ r: 4, stroke: "#0A0A0C", strokeWidth: 2, fill: COLOR_BENCHMARK }}
                />
              ) : null}
              {showFairValue && hasFairValue ? (
                <Line
                  yAxisId="currency"
                  type="monotone"
                  name={t("portfolio.chart.estimated_fair_value")}
                  dataKey="fair_value"
                  stroke={COLOR_FAIR_VALUE}
                  strokeWidth={2}
                  // Phase 8.E.4 visual-review v6 — Fair Value is now
                  // solid green; the dashed styling has moved to the
                  // S&P 500 benchmark line to reflect the final visual
                  // hierarchy (Portfolio + Fair Value = primary solid
                  // lines, benchmark = secondary dashed line).
                  dot={false}
                  connectNulls={false}
                  activeDot={{ r: 4, stroke: "#0A0A0C", strokeWidth: 2, fill: COLOR_FAIR_VALUE }}
                  style={{ filter: "drop-shadow(0 0 12px rgba(43,255,138,0.45)) drop-shadow(0 0 28px rgba(43,255,138,0.22))" }}
                />
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="portfolio-perf-loading-state">
            {loading ? "…" : t("portfolio.chart.not_enough_history")}
          </div>
        )}
        {/* Phase 8.WLNAV.v1.6 — Static preview tooltip.
            Pinned near the right edge, vertically centered, showing
            the last data point. Reuses <GlassTooltip> to guarantee
            1:1 styling with the real hover tooltip. Non-interactive:
            pointer-events:none so it never blocks the CTA overlay. */}
        {previewMode && chartData.length >= 2 ? (
          <div
            data-testid="portfolio-preview-static-tooltip"
            className="hidden md:block absolute pointer-events-none z-20"
            style={{ right: `${chartRightMargin + 8}px`, top: "48%", transform: "translateY(-50%)" }}
          >
            <GlassTooltip
              active
              payload={[{ payload: chartData[chartData.length - 1] }]}
              label={chartData[chartData.length - 1].date}
              t={t}
              rangeLabel={rangeLabel}
              portfolioReturnPct={portfolioReturnPct}
              benchmarkReturnPct={benchmarkReturnPct}
              relativePctPoints={relativePctPoints}
              fvCoveragePct={fvCoveragePct}
              cashFlowsByDate={cashFlowsByDate}
              isMobile={false}
              containerWidth={containerWidth}
            />
          </div>
        ) : null}
      </div>

      {/* Value-mode benchmark note — clarifies why the S&P line is hidden. */}
      {isValueMode ? (
        <div className="mt-3 text-[11px] text-muted-foreground/80" data-testid="portfolio-perf-value-mode-benchmark-note">
          {t("portfolio.chart.value_mode_benchmark_note")}
        </div>
      ) : null}

      {/* Phase 8.E.4 visual-review v8 — plain-language explanation
          of the Performance-Index scale. Rendered in index mode only,
          right above the benchmark disclaimer, so users understand
          that "143" means +43 % relative to the selected range's
          start rather than an absolute value. */}
      {!isValueMode && hasPortfolioLine ? (
        <div className="mt-3 text-[11px] text-muted-foreground/85 leading-snug" data-testid="portfolio-perf-index-explanation">
          {t("portfolio.chart.index_explanation")}
        </div>
      ) : null}

      {/* Phase 8.E.4 visual-review v6 — S&P 500 benchmark role
          disclaimer (index mode only, when a benchmark line is
          actually drawn). Clarifies that the benchmark is a price
          index without reinvested dividends. */}
      {!isValueMode && hasBenchmarkLine ? (
        <div className="mt-2 text-[11px] text-muted-foreground/70" data-testid="portfolio-perf-benchmark-disclaimer">
          {t("portfolio.chart.benchmark_disclaimer")}
        </div>
      ) : null}

      {/* Phase 8.E.4 visual-review v9 — the older standalone
          `sp500_disclaimer` sentence was removed here because the
          newer `benchmark_disclaimer` above (rendered under the
          index-explanation block) already contains the identical
          "price index / does not include reinvested dividends"
          caveat. Keeping both created a duplicate sentence in EN + DE.
          The `sp500_disclaimer` i18n key is left intact for any
          other consumer that may still reference it. */}
      {/* Fair-value disclosure ONLY when the series is present (per spec). */}
      {hasFairValue && showFairValue ? (
        <div className="mt-1 text-[11px] text-muted-foreground/80 leading-relaxed max-w-3xl" data-testid="portfolio-perf-fv-interpretation">
          {t("portfolio.chart.fv_interpretation_note")}
        </div>
      ) : null}
    </div>
  );
}

/** Header: title + subtitle + method badge + quality chip + KPIs + range tabs. */
function ChartHeader({
  t, range, onRangeClick, loading,
  method, fallbackUsed, dataQuality,
  portfolioReturnPct, benchmarkReturnPct, relativePctPoints,
  hasBenchmarkLine,
  showFairValueToggle, showFairValue, setShowFairValue,
  // Rev-2 props (accepted so caller stays in sync; the baseline note
  // itself is rendered by the parent, not inside the header).
  baselineDate: _baselineDate, preBaselineExcludedCount: _preBaselineExcludedCount,
  // Phase 8.E.4 visual-review — value mode (Modified Dietz fallback).
  isValueMode = false,
  historyBuilding = false,
  // Phase 8.E.4 visual-review v3 — container-width mobile flag.
  isMobile = false,
  // Phase 8.WLNAV.v1.6 — Locked preview mode.
  previewMode = false,
}) {
  const pTone = portfolioReturnPct === null || portfolioReturnPct === undefined ? "neutral"
    : portfolioReturnPct > 0 ? "positive"
    : portfolioReturnPct < 0 ? "negative" : "neutral";
  const bTone = benchmarkReturnPct === null || benchmarkReturnPct === undefined ? "neutral"
    : benchmarkReturnPct > 0 ? "positive"
    : benchmarkReturnPct < 0 ? "negative" : "neutral";
  const rTone = relativePctPoints === null || relativePctPoints === undefined ? "neutral"
    : relativePctPoints > 0 ? "positive"
    : relativePctPoints < 0 ? "negative" : "neutral";
  // Phase 8.E.4 visual-review — hide the method + quality badges when
  // the calculation could not produce a valid method (no stable
  // baseline, insufficient history, etc.). Showing "Modified Dietz" or
  // "TWR" in those states misleads the user.
  const hasValidMethod = method && method !== "NONE"
    && (portfolioReturnPct !== null && portfolioReturnPct !== undefined);
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-start justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <div className="font-display text-lg sm:text-xl" data-testid="portfolio-growth-chart-title">
            {t("portfolio.chart.title")}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {isValueMode
              ? t("portfolio.chart.value_mode_subtitle")
              : t("portfolio.chart.subtitle")}
          </div>
          {hasValidMethod ? (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <MethodBadge
                method={method}
                fallbackUsed={fallbackUsed}
                dataQuality={dataQuality}
                t={t}
                isValueMode={isValueMode}
              />
              {dataQuality ? <QualityChip dataQuality={dataQuality} t={t} historyBuilding={historyBuilding} /> : null}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {showFairValueToggle ? (
            <button
              type="button"
              onClick={previewMode ? undefined : () => setShowFairValue(v => !v)}
              disabled={previewMode}
              aria-disabled={previewMode ? "true" : undefined}
              data-testid="portfolio-perf-fv-toggle"
              data-preview-locked={previewMode ? "true" : undefined}
              className={`inline-flex items-center gap-1 text-[11px] rounded-full border border-white/10 bg-black/25 px-2.5 py-1 text-muted-foreground ${
                previewMode
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:text-foreground hover:bg-white/5"
              }`}
            >
              {previewMode ? <Lock className="w-3 h-3 opacity-80" /> : null}
              {showFairValue
                ? t("portfolio.chart.fair_value_toggle_hide")
                : t("portfolio.chart.fair_value_toggle_show")}
            </button>
          ) : null}
          <div
            data-testid="portfolio-growth-timeframe-tabs"
            data-preview-locked={previewMode ? "true" : undefined}
            className="inline-flex flex-wrap rounded-full border border-white/10 bg-black/30 p-1 backdrop-blur"
          >
            {RANGES.map((r) => {
              const isActive = range === r;
              const isLockedInPreview = previewMode && !isActive;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={isLockedInPreview ? undefined : () => onRangeClick(r)}
                  disabled={loading || isLockedInPreview}
                  aria-disabled={isLockedInPreview ? "true" : undefined}
                  data-testid={`portfolio-growth-timeframe-${r.toLowerCase()}`}
                  data-preview-locked={isLockedInPreview ? "true" : undefined}
                  title={isLockedInPreview ? t("portfolio.chart.preview_locked_range") : undefined}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-medium transition-colors duration-150 ${
                    isActive
                      ? "bg-black/40 text-foreground shadow-[0_0_0_1px_rgba(61,235,255,0.45),0_0_18px_rgba(61,235,255,0.18)]"
                      : isLockedInPreview
                        ? "text-muted-foreground/55 cursor-not-allowed"
                        : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                  } ${loading && !isLockedInPreview ? "opacity-70 cursor-progress" : ""}`}
                >
                  {isLockedInPreview ? <Lock className="w-2.5 h-2.5 opacity-70" /> : null}
                  {r}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* KPI row.
          Phase 8.E.4 visual-review v4: container-mobile aware layout.
          On narrow containers we use a 2+1 grid so cards stay wide
          enough for their labels + values without wrapping, while
          taking full advantage of the reclaimed axis-title space:
              [ Portfolio ] [ Benchmark ]
              [       Relative Δ        ]
          On wider containers the classic 3-across layout still
          applies. `sm:grid-cols-3` (viewport-based) is intentionally
          gone — it was the layout-overlap culprit reported earlier. */}
      <div
        data-testid="portfolio-perf-kpi-row"
        className={`mt-3 grid gap-2 ${isMobile ? "grid-cols-2" : "grid-cols-3"}`}
      >
        <KPICard
          label={t("portfolio.chart.kpi_portfolio_return")}
          value={portfolioReturnPct === null || portfolioReturnPct === undefined
            ? t("portfolio.chart.kpi_unavailable")
            : formatPct(portfolioReturnPct)}
          tone={pTone === "neutral" ? "portfolio" : pTone}
          testId="portfolio-perf-kpi-portfolio"
          unavailable={portfolioReturnPct === null || portfolioReturnPct === undefined}
        />
        <KPICard
          label={t("portfolio.chart.kpi_benchmark_return")}
          value={(isValueMode || hasBenchmarkLine) && benchmarkReturnPct !== null && benchmarkReturnPct !== undefined
            ? formatPct(benchmarkReturnPct)
            : t("portfolio.chart.kpi_unavailable")}
          tone={bTone === "neutral" ? "benchmark" : bTone}
          testId="portfolio-perf-kpi-benchmark"
          unavailable={(!isValueMode && !hasBenchmarkLine) || benchmarkReturnPct === null || benchmarkReturnPct === undefined}
        />
        <KPICard
          label={t("portfolio.chart.kpi_relative_performance")}
          value={(isValueMode || hasBenchmarkLine) && relativePctPoints !== null && relativePctPoints !== undefined
            ? formatPct(relativePctPoints)
            : t("portfolio.chart.kpi_unavailable")}
          tone={rTone}
          hint={t("portfolio.chart.kpi_relative_performance_hint")}
          testId="portfolio-perf-kpi-relative"
          unavailable={(!isValueMode && !hasBenchmarkLine) || relativePctPoints === null || relativePctPoints === undefined}
          fullWidth={isMobile}
        />
      </div>
    </div>
  );
}


